import { StatusChipProps } from '/packages/shared-components/StatusChip/StatusChip';
import {
  ComponentPermissions,
  getAllPartnersAdminQuery,
  getPartnersByIdsQuery,
  partnerAdminQuery,
  UserStatus,
  Workstream,
} from '~src/generated/portal/graphql';
import { getAllWorkstreams } from '/packages/shared-components/utils/workstreams';
import { formatWorkstream } from '/packages/shared-components/utils/util';

export const wsComponentKey = Object.freeze({
  sourceCodeAccess: 'sourceCodeAccess',
  eligibilityApiAccess: 'eligibilityAPIAccess',
  accountRecoveryApiAccess: 'accountRecoveryApiAccess',
  activationOAuthAccess: 'activationOAuthAccess',
  disneyDrivenActivationAccess: 'disneyDrivenActivationAccess',
  onDeviceActivationAccess: 'onDeviceActivationAccess',
  contentProviderAdditionalFieldsAccess: 'contentProviderAdditionalFieldsAccess',
  entitlementPushBasedApiAccess: 'entitlementPushBasedApiAccess',
  refundsAndChargebacksPushBasedApiAccess: 'refundsAndChargebacksPushBasedApiAccess',
});

export const workstreamComponents = Object.freeze({
  sourceCodeAccess: {
    label: 'ADK Code Access',
    value: wsComponentKey.sourceCodeAccess,
    description:
      'Grants required access to ADK source code. This component is automatically enabled with ADK Integration and can be customized at the user level.',
    auto: true,
  },
  eligibilityApiAccess: {
    label: 'Eligibility API',
    value: wsComponentKey.eligibilityApiAccess,
    partnerLevel: true,
  },
  activationOAuthAccess: {
    label: 'Activation OAuth',
    value: wsComponentKey.activationOAuthAccess,
    partnerLevel: true,
  },
  disneyDrivenActivationAccess: {
    label: 'Disney Driven Activation',
    value: wsComponentKey.disneyDrivenActivationAccess,
    partnerLevel: true,
  },
  accountRecoveryApiAccess: {
    label: 'Account Recovery API',
    value: wsComponentKey.accountRecoveryApiAccess,
    partnerLevel: true,
  },
  onDeviceActivationAccess: {
    label: 'On Device Activation',
    value: wsComponentKey.onDeviceActivationAccess,
    partnerLevel: true,
  },
  contentProviderAdditionalFieldsAccess: {
    label: 'Additional Fields',
    value: wsComponentKey.contentProviderAdditionalFieldsAccess,
    partnerLevel: true,
  },
  entitlementPushBasedApiAccess: {
    label: 'Entitlement Push-Based API',
    value: wsComponentKey.entitlementPushBasedApiAccess,
    partnerLevel: true,
  },
  refundsAndChargebacksPushBasedApiAccess: {
    label: 'Refunds and Chargebacks Push-Based API',
    value: wsComponentKey.refundsAndChargebacksPushBasedApiAccess,
    partnerLevel: true,
  },
});

export const userStatusColor = (status: UserStatus): StatusChipProps['color'] => {
  switch (status) {
    case UserStatus.Active:
      return 'success';
    case UserStatus.Pending:
    case UserStatus.Requested:
      return 'warning';
    case UserStatus.Reset_Required:
      return 'info';
    case UserStatus.Deactivated:
      return 'error';
    default:
      return 'none';
  }
};

export const formatUserStatusLabel = (status: UserStatus) => {
  const statusStr = status.slice(0, 1).toUpperCase().concat(status.slice(1));
  if (status === UserStatus.Password_Reset_Sent) {
    return statusStr.replace('Password', 'PWD');
  }
  return statusStr;
};

export const getUniqueWorkstreams = (workstreams: string[]) => {
  return Array.from(
    new Set(
      workstreams.filter((e) =>
        ['Billing_API', 'ADK_Integration', 'Android_TV', 'Content_Provider', 'Perks', 'Browser_Based_Device'].includes(
          e,
        ),
      ),
    ),
  );
};

export function removeAtIndex(arr: string[]) {
  return arr.map((_, index) => arr.filter((_, i) => i !== index));
}

export const getCognitoErrorMessage = (obj: object, defaultMsg = '') => {
  if (!Object.hasOwn(obj, 'message')) return defaultMsg;
  return (obj as { message: string }).message;
};

export type TPermission = {
  label: string;
  value: string;
  checked: boolean;
  tooltip?: string;
  children?: TComponentPermission[];
};

export type TComponentPermission = Omit<TPermission, 'children'> & {
  partnerLevel?: boolean;
  auto?: boolean;
};

export const getNestedPermissionValue = (
  permissions: TPermission[],
  permission: (typeof wsComponentKey)[keyof typeof wsComponentKey],
) => {
  for (const p of permissions) {
    if (p.children?.length) {
      const access = p.children.find((c) => c.value === permission);
      if (access) return access.checked;
    }
  }
  return false;
};

const getAutoComponentChecked = (
  ws: string,
  permissions: string[],
  componentKey: string,
  componentPermissions: ComponentPermissions[] | undefined,
  typename: ComponentPermissions['__typename'],
  fieldName: string,
  auto?: boolean,
): boolean => {
  if (!permissions.includes(ws)) return false;

  if (componentPermissions) {
    const explicitPermission = componentPermissions.find((p) => p.__typename === typename);
    if (explicitPermission) {
      return Boolean((explicitPermission as unknown as Record<string, boolean>)[fieldName]);
    }
    return !!auto;
  }

  return permissions.includes(componentKey);
};

/**
 * Create the children elements for a specific workstream of a `partner`-`user` set
 *
 * @param ws Workstream
 * @param permissions List of permissions for the user (including `workstreams` and `component permissions`)
 * @param components List of the components available for the Partner Space
 * @returns List of component permissions
 */
export const genChildrenPermissions = (
  ws: TPermission['value'],
  permissions: string[] = [],
  components: string[] = [],
  componentPermissions?: ComponentPermissions[],
): TComponentPermission[] | undefined => {
  // NOTE: Add new component permissions here
  switch (ws) {
    case 'ADK_Integration': {
      const comps: TComponentPermission[] = [
        {
          ...workstreamComponents.sourceCodeAccess,
          checked: getAutoComponentChecked(
            ws,
            permissions,
            wsComponentKey.sourceCodeAccess,
            componentPermissions,
            'ADKSourceCodePermission',
            'sourceCodeAccess',
            workstreamComponents.sourceCodeAccess.auto,
          ),
        },
      ];
      if (components.includes(wsComponentKey.onDeviceActivationAccess)) {
        comps.push({
          ...workstreamComponents.onDeviceActivationAccess,
          checked: true,
          tooltip: `This setting can't be changed on individual users. It's only managed through Partner Space Edition.`,
        });
      }
      return comps;
    }
    case 'Billing_API':
      if (workstreamComponents.eligibilityApiAccess.partnerLevel) {
        const comps: TComponentPermission[] = [];
        if (components.includes(wsComponentKey.eligibilityApiAccess)) {
          comps.push({
            ...workstreamComponents.eligibilityApiAccess,
            checked: true,
            tooltip: `This setting can't be changed on individual users. It's only managed through Partner Space Edition.`,
          });
        }
        if (components.includes(wsComponentKey.activationOAuthAccess)) {
          comps.push({
            ...workstreamComponents.activationOAuthAccess,
            checked: true,
            tooltip: `This setting can't be changed on individual users. It's only managed through Partner Space Edition.`,
          });
        }
        if (components.includes(wsComponentKey.disneyDrivenActivationAccess)) {
          comps.push({
            ...workstreamComponents.disneyDrivenActivationAccess,
            checked: true,
            tooltip: `This setting can't be changed on individual users. It's only managed through Partner Space Edition.`,
          });
        }
        if (components.includes(wsComponentKey.accountRecoveryApiAccess)) {
          comps.push({
            ...workstreamComponents.accountRecoveryApiAccess,
            checked: true,
            tooltip: `This setting can't be changed on individual users. It's only managed through Partner Space Edition.`,
          });
        }
        return comps;
      }
      break;
    case 'Content_Provider': {
      const comps: TComponentPermission[] = [];
      if (components.includes(wsComponentKey.contentProviderAdditionalFieldsAccess)) {
        comps.push({
          ...workstreamComponents.contentProviderAdditionalFieldsAccess,
          checked: true,
          tooltip: `This setting can't be changed on individual users. It's only managed through Partner Space Edition.`,
        });
      }
      if (components.includes(wsComponentKey.entitlementPushBasedApiAccess)) {
        comps.push({
          ...workstreamComponents.entitlementPushBasedApiAccess,
          checked: true,
          tooltip: `This setting can't be changed on individual users. It's only managed through Partner Space Edition.`,
        });
      }
      if (components.includes(wsComponentKey.refundsAndChargebacksPushBasedApiAccess)) {
        comps.push({
          ...workstreamComponents.refundsAndChargebacksPushBasedApiAccess,
          checked: true,
        });
      }
      return comps;
    }
  }
  return undefined;
};

/**
 * Returns a new TPermission result changing its children when is required
 *
 * For use in `UserPermissions` component
 *
 * @param permission
 * @param checked
 * @returns
 */
export const permissionChange = (permission: TPermission, checked: boolean): TPermission => {
  if (permission.children) {
    return {
      ...permission,
      checked,
      children: permission.children.map((ch) => {
        if (ch.partnerLevel) return ch;
        return { ...ch, checked: !checked ? false : ch.auto ? true : ch.checked };
      }),
    };
  }
  return { ...permission, checked };
};
type QPartnerById = getPartnersByIdsQuery['partners'][0];
type QAllPartnersAdmin = getAllPartnersAdminQuery['partners'][0];
type QPartner = NonNullable<partnerAdminQuery['partner']>;
type QPartnerAdmin = partnerAdminQuery['partner'];

/**
 * Extract components from a partner
 *
 * @param partner Partner query result
 * @returns List of components
 */
export const extractComponents = (
  partner: QPartnerById | QPartnerAdmin | QPartner | QAllPartnersAdmin | undefined,
): string[] => {
  if (!partner) return [];
  return partner.componentPermissions.reduce<string[]>((prev, p) => {
    switch (p.__typename) {
      case 'ADKSourceCodePermission':
        return p.sourceCodeAccess ? [...prev, wsComponentKey.sourceCodeAccess] : prev;
      case 'EligibilityAPIPermission':
        return p.eligibilityAPIAccess ? [...prev, wsComponentKey.eligibilityApiAccess] : prev;
      case 'AccountRecoveryAPIPermission':
        return p.accountRecoveryApiAccess ? [...prev, wsComponentKey.accountRecoveryApiAccess] : prev;
      case 'ActivationOAuthPermission':
        return p.activationOAuthAccess ? [...prev, wsComponentKey.activationOAuthAccess] : prev;
      case 'DisneyDrivenActivationPermission':
        return p.ddaAccess ? [...prev, wsComponentKey.disneyDrivenActivationAccess] : prev;
      case 'OnDeviceActivationPermission':
        return p.onDeviceActivationAccess ? [...prev, wsComponentKey.onDeviceActivationAccess] : prev;
      case 'ContentProviderAdditionalFieldsPermission':
        return p.contentProviderAdditionalFieldsAccess
          ? [...prev, wsComponentKey.contentProviderAdditionalFieldsAccess]
          : prev;
      case 'EntitlementPushBasedApiPermission':
        return p.entitlementPushBasedApiAccess ? [...prev, wsComponentKey.entitlementPushBasedApiAccess] : prev;
      case 'RefundsAndChargebacksPushBasedApiPermission':
        return p.refundsAndChargebacksPushBasedApiAccess
          ? [...prev, wsComponentKey.refundsAndChargebacksPushBasedApiAccess]
          : prev;
      default:
        return prev;
    }
  }, []);
};

export const extractUserPermissionStrings = (componentPermissions: ComponentPermissions[]): string[] => {
  return componentPermissions.reduce<string[]>((prev, curr) => {
    switch (curr.__typename) {
      case 'ADKSourceCodePermission':
        return curr.sourceCodeAccess ? [...prev, wsComponentKey.sourceCodeAccess] : prev;
      case 'EligibilityAPIPermission':
        return curr.eligibilityAPIAccess ? [...prev, wsComponentKey.eligibilityApiAccess] : prev;
      case 'AccountRecoveryAPIPermission':
        return curr.accountRecoveryApiAccess ? [...prev, wsComponentKey.accountRecoveryApiAccess] : prev;
      case 'ActivationOAuthPermission':
        return curr.activationOAuthAccess ? [...prev, wsComponentKey.activationOAuthAccess] : prev;
      case 'DisneyDrivenActivationPermission':
        return curr.ddaAccess ? [...prev, wsComponentKey.disneyDrivenActivationAccess] : prev;
      case 'OnDeviceActivationPermission':
        return curr.onDeviceActivationAccess ? [...prev, wsComponentKey.onDeviceActivationAccess] : prev;
      case 'ContentProviderAdditionalFieldsPermission':
        return curr.contentProviderAdditionalFieldsAccess
          ? [...prev, wsComponentKey.contentProviderAdditionalFieldsAccess]
          : prev;
      case 'EntitlementPushBasedApiPermission':
        return curr.entitlementPushBasedApiAccess ? [...prev, wsComponentKey.entitlementPushBasedApiAccess] : prev;
      case 'RefundsAndChargebacksPushBasedApiPermission':
        return curr.refundsAndChargebacksPushBasedApiAccess
          ? [...prev, wsComponentKey.refundsAndChargebacksPushBasedApiAccess]
          : prev;
      case 'WorkstreamPermission':
        return [...prev, curr.workstream];
      default:
        return prev;
    }
  }, []);
};

export const getSelectedWorkstreams = (permissions: TPermission[]): Workstream[] => {
  return permissions.reduce<Workstream[]>((prev, curr) => {
    if (curr.checked) return [...prev, curr.value as Workstream] satisfies Workstream[];
    return prev satisfies Workstream[];
  }, []);
};

export type TWorkstreamInput = {
  label: string;
  value: string;
  description?: string;
  components?: TWorkstreamComponent[];
};

export type TWorkstreamComponent = {
  label: string;
  description: string;
  value: string;
  auto?: boolean;
  parentComponent?: string;
};

export const allWorkstreams: TWorkstreamInput[] = [
  {
    label: formatWorkstream(Workstream.ADK_Integration),
    value: Workstream.ADK_Integration,
    description:
      'Enables the Application Development Kit (ADK) process, which allows partners to build and certify a custom Disney application for their devices as part of the integration workflow.',
    components: [
      workstreamComponents.sourceCodeAccess,
      {
        label: 'On Device Activation',
        description:
          'On-Device Activation (ODA) lets customers finish Disney+ setup on the same partner set-top box where they subscribed, so they can go from purchase to activation to watching without leaving the device.',
        value: wsComponentKey.onDeviceActivationAccess,
      },
    ],
  },
  {
    label: formatWorkstream(Workstream.Android_TV),
    value: Workstream.Android_TV,
    description:
      'Provides access to the Disney+ APK for Android TV, used to deploy the app on set-top boxes (STBs) running the Android TV OS.',
  },
  {
    label: formatWorkstream(Workstream.Browser_Based_Device),
    value: Workstream.Browser_Based_Device,
    description:
      'Provides access for documentation and support for the BBD device program. These devices, which include Smart TVs and Set Top Boxes with embedded browsers, launch the Disney BBD application, running it within the device’s built-in browser.',
  },
  {
    label: formatWorkstream(Workstream.Content_Provider),
    value: Workstream.Content_Provider,
    description:
      'Enables the API that allows partners to sell access to Disney Streaming content through their own websites or applications as part of the integration process.',
    components: [
      {
        label: 'Additional Fields',
        description: 'Content Provider Additional Fields documentation. (NextRenewalDate)',
        value: wsComponentKey.contentProviderAdditionalFieldsAccess,
      },
      {
        label: 'Entitlement Push-Based API',
        description: 'Enables visibility of the Push-Based API documentation.',
        value: wsComponentKey.entitlementPushBasedApiAccess,
      },
      {
        label: 'Refunds and Chargebacks Push-Based API',
        description: 'Enables visibility of the Refunds and Chargebacks Push-Based API documentation.',
        value: wsComponentKey.refundsAndChargebacksPushBasedApiAccess,
        parentComponent: wsComponentKey.entitlementPushBasedApiAccess,
      },
    ],
  },
  {
    label: 'Partner API',
    value: Workstream.Billing_API,
    description:
      'Provides access to Disney’s API suite for managing subscription lifecycles and customer integration, including account creation, activation notifications, subscription updates, and bulk data reconciliation.',
    components: [
      {
        label: 'Eligibility API',
        description:
          'Allows partners to verify if a user qualifies for specific offers—such as access to subscriber-only content or gated products—using Disney Streaming’s API as part of the integration. Enabling this option grants access to all users under this partner space.',
        value: wsComponentKey.eligibilityApiAccess,
      },
      {
        label: 'Activation OAuth',
        description:
          'Enables select Partners to use OAuth for the Activation flow. This supports an alternative User Experience, in which the user starts and finishes the Activation process on the Partner site, instead of finishing the flow on the Disney streaming site. This requires partners to support OAuth integrations and make a backend Activation API call, instead of redirecting the user to the Disney website with an Activation token in the URL.',
        value: wsComponentKey.activationOAuthAccess,
      },
      {
        label: 'Disney Driven Activation',
        description:
          'DDA enables Disney to supplement partner tactics to encourage enrolled subscribers to activate their subscriptions.',
        value: wsComponentKey.disneyDrivenActivationAccess,
      },
      {
        label: 'Account Recovery API',
        description:
          'Enables access to the Account Recovery API which helps Partner subscribers who cannot locate their Disney+ accounts.',
        value: wsComponentKey.accountRecoveryApiAccess,
      },
    ],
  },
  {
    label: formatWorkstream(Workstream.Perks),
    value: Workstream.Perks,
    description:
      'Provides access to Disney+ Perks, a loyalty program for streaming subscribers offering discounts, early access, exclusive prizes, and more—highlighted on the dedicated site',
  },
];
